/**
 * Animation durations: stored and specified in milliseconds, played as whole
 * video frames of the switcher's mode (frame-locked, see the bridge's
 * frame-clock). The same duration therefore takes the same time at 25p and
 * 50p; only the number of animation steps differs.
 *
 * Durations used to be stored as frame counts, which ran at a fixed rate
 * before the frame lock: SuperSource layout transitions (animator / smart
 * transitions) on a 33 ms timer (~30 fps), the playout squeeze at 25 fps.
 * Old values are converted at those rates so they keep their speed.
 */
import { z } from "zod";
/** Rate `duration_frames` of layout transitions ran at before the frame lock. */
export const LEGACY_TRANSITION_FPS = 30;
/** Rate playout `in/out_duration_frames` ran at before the frame lock. */
export const LEGACY_PLAYOUT_FPS = 25;
/** Transition length when none is given (was 30 frames at 30 fps). */
export const DEFAULT_TRANSITION_MS = 1000;
/** Default squeeze in/out (was 25 frames at 25 fps). */
export const DEFAULT_SQUEEZE_MS = 1000;
/** Longest accepted animation. */
export const MAX_ANIMATION_MS = 60_000;
/** Old frame count -> integer ms at the rate it used to run at. */
export function legacyFramesToMs(frames, fps) {
    if (!Number.isFinite(frames) || frames <= 0)
        return 0;
    return Math.round((frames * 1000) / fps);
}
/**
 * Whole video frames for a duration: max(1, round(ms / frameMs)). With
 * `allowZero`, 0 ms (or less) is 0 frames, i.e. an instant cut.
 */
export function animationFrameCount(durationMs, frameMs, options = {}) {
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
        return options.allowZero ? 0 : 1;
    }
    return Math.max(1, Math.round(durationMs / frameMs));
}
/** Seconds an animation actually takes on a switcher with `frameMs` frames. */
export function animationSeconds(durationMs, frameMs) {
    return (animationFrameCount(durationMs, frameMs) * frameMs) / 1000;
}
/** Frames per second of a frame length, for labels: 20 ms -> "50". */
export function formatFrameRate(frameMs) {
    const fps = 1000 / frameMs;
    return Number.isInteger(Math.round(fps * 100) / 100)
        ? String(Math.round(fps))
        : fps.toFixed(2);
}
/**
 * A transition with its duration in ms. `duration_ms` wins; a deprecated
 * `duration_frames` is converted at LEGACY_TRANSITION_FPS, so old configs
 * and old Companion builds keep their speed. `legacy` tells whether the old
 * field was used.
 */
export function normalizeLayoutTransition(transition) {
    const { duration_frames: frames, ...rest } = transition;
    if (typeof rest.duration_ms === "number" || typeof frames !== "number") {
        return { transition: rest, legacy: false };
    }
    return {
        transition: {
            ...rest,
            duration_ms: legacyFramesToMs(frames, LEGACY_TRANSITION_FPS),
        },
        legacy: true,
    };
}
/** Stored transition (any age) -> current shape. Null/invalid -> cut. */
export function migrateLayoutTransition(value) {
    if (!value || typeof value !== "object" || !("type" in value)) {
        return { type: "cut" };
    }
    return normalizeLayoutTransition(value).transition;
}
const durationMsSchema = z.number().int().min(0).max(MAX_ANIMATION_MS);
/** Deprecated frame counts: not limited to integers, like before. */
const legacyFramesSchema = z.number().min(0);
/**
 * LayoutTransition as accepted by the API. `duration_frames` is deprecated
 * but accepted (frames at 30 fps); run the result through
 * normalizeLayoutTransition.
 */
export const layoutTransitionInputSchema = z.object({
    type: z.enum(["cut", "zoom", "fade", "push", "wipe", "smart"]),
    duration_ms: durationMsSchema.optional(),
    /** @deprecated frames at 30 fps; use duration_ms */
    duration_frames: legacyFramesSchema.optional(),
    direction: z.enum(["left", "right", "up", "down"]).optional(),
    easing: z.enum(["linear", "ease-in", "ease-out", "ease-in-out"]).optional(),
    other_boxes_effect: z.enum(["slide", "scale", "fade"]).optional(),
    other_boxes_direction: z
        .enum(["left", "right", "up", "down", "outward"])
        .optional(),
});
/**
 * Playout fields with squeeze durations in ms. `*_ms` wins; deprecated
 * `*_frames` are converted at LEGACY_PLAYOUT_FPS.
 */
export function normalizePlayoutDurations(data) {
    const { in_duration_frames: inFrames, out_duration_frames: outFrames, ...rest } = data;
    let legacy = false;
    const out = {
        ...rest,
    };
    if (rest.in_duration_ms === undefined && typeof inFrames === "number") {
        out.in_duration_ms = legacyFramesToMs(inFrames, LEGACY_PLAYOUT_FPS);
        legacy = true;
    }
    if (rest.out_duration_ms === undefined && typeof outFrames === "number") {
        out.out_duration_ms = legacyFramesToMs(outFrames, LEGACY_PLAYOUT_FPS);
        legacy = true;
    }
    return { data: out, legacy };
}
/** Zod fields for squeeze durations, deprecated frame fields included. */
export const playoutDurationInputFields = {
    in_duration_ms: durationMsSchema.optional(),
    out_duration_ms: durationMsSchema.optional(),
    /** @deprecated frames at 25 fps; use in_duration_ms */
    in_duration_frames: z.number().int().min(0).max(300).optional(),
    /** @deprecated frames at 25 fps; use out_duration_ms */
    out_duration_frames: z.number().int().min(0).max(300).optional(),
};
